import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { onboardingApi } from '../../api/onboarding';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Check } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const CATEGORY_LABELS: Record<string, string> = {
  hr_documentation: 'HR Documentation', contract: 'Contract', identification_verification: 'Identification Verification',
  bank_payroll: 'Bank/Payroll', it_account_setup: 'IT/Account Setup', equipment_assets: 'Equipment/Assets',
  orientation: 'Orientation', policies_conduct: 'Policies/Code of Conduct', safeguarding_psea: 'Safeguarding/PSEA',
  health_safety: 'Health and Safety', department_induction: 'Department Induction', supervisor_responsibilities: 'Supervisor Responsibilities',
};

/** Staff can see their own onboarding progress, and complete any task genuinely assigned to them (e.g. "acknowledge Code of Conduct"). Everything else is HR-managed. */
export function MyOnboardingPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const onboardingQuery = useQuery({ queryKey: ['my-onboarding'], queryFn: () => onboardingApi.mine() });

  const completeTaskMutation = useMutation({
    mutationFn: (taskId: number) => onboardingApi.completeTask(taskId, { status: 'completed' }),
    onSuccess: () => { showSuccess('Task marked complete.'); queryClient.invalidateQueries({ queryKey: ['my-onboarding'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed - this task may not be assigned to you.'),
  });

  return (
    <div className="page">
      <Link to="/staff">&larr; Back to Dashboard</Link>
      <h1>My Onboarding</h1>

      {onboardingQuery.data?.data === null && (
        <section className="card"><p className="muted">No onboarding checklist has been started for you yet. Contact HR if this seems wrong.</p></section>
      )}

      {onboardingQuery.data?.data && (
        <section className="card">
          <h2>Progress - {onboardingQuery.data.data.status === 'completed' ? 'Completed' : 'In Progress'}</h2>
          <DataTable
            rows={onboardingQuery.data.tasks}
            rowKey={(t) => t.id}
            emptyMessage="No tasks."
            columns={[
              { header: 'Category', render: (t) => CATEGORY_LABELS[t.category] ?? t.category },
              { header: 'Task', render: (t) => t.task_name },
              { header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
              { header: '', render: (t) => t.status === 'pending' && <IconActionButton icon={Check} label="Mark Done" onClick={() => completeTaskMutation.mutate(t.id)} /> },
            ]}
          />
        </section>
      )}
    </div>
  );
}
